import { useEffect, useRef, useState } from "react";
import * as THREE from "three";
import { EffectComposer } from "three/addons/postprocessing/EffectComposer.js";
import { RenderPass } from "three/addons/postprocessing/RenderPass.js";
import { UnrealBloomPass } from "three/addons/postprocessing/UnrealBloomPass.js";
import { OutputPass } from "three/addons/postprocessing/OutputPass.js";
import { getLogFrequencyBins } from "../utils/logarithmicFrequencies";
import { readLightFrameAudio, type LightFrameFrequencyRange } from "../visuals/lightFrameAudio";
import { FRAME_COUNT, FRAME_SPACING, LightFrameEngine, type ReactingMode } from "../visuals/lightFrameEngine";
import { createLightFrame } from "../visuals/lightStageGeometry";
import { lightBarRadiance } from "../visuals/kickWave";
import { createMatteFloor, createMist, createFilmGrain } from "../visuals/lightStageAtmosphere";

// Rebuild the GPU scene when its factories hot-update, preserving audio playback.
const sceneFactories = { createLightFrame, LightFrameEngine };

interface Props extends LightFrameFrequencyRange {
  mode: ReactingMode;
  rotationEnabled: boolean;
  threshold: number;
  playing: boolean;
  sampleRate: number;
  getFrequencyData: () => Float32Array;
  onBassIntensityChange?: (value: number, triggered: boolean) => void;
  onHighIntensityChange?: (value: number, triggered: boolean) => void;
}

/** One WebGL canvas owns the tubes, diffuse floor lighting, haze and film grain. */
export function ThreeLightStage(props: Props) {
  const container = useRef<HTMLDivElement>(null);
  const current = useRef(props);
  current.current = props;
  const [error, setError] = useState<string | null>(null);
  const { getFrequencyData, sampleRate } = props;

  useEffect(() => {
    const host = container.current;
    if (!host) return;
    let renderer: THREE.WebGLRenderer;
    try {
      // Scene antialiasing is handled by the composer target, not a second MSAA buffer.
      renderer = new THREE.WebGLRenderer({ antialias: false, powerPreference: "high-performance" });
    } catch {
      setError("无法创建 WebGL 场景，请开启浏览器硬件加速后重试。");
      return;
    }
    setError(null);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 1.25));
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 0.95;
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    renderer.domElement.setAttribute("aria-label", "冰色光柱 3D 音乐场景");
    host.appendChild(renderer.domElement);

    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0x000000);
    scene.fog = new THREE.FogExp2(0x000000, 0.019);
    const camera = new THREE.PerspectiveCamera(48, 1, 0.1, 250);
    camera.position.set(0, 1.6, 12);
    camera.lookAt(0, 1.9, -30);

    const frames = Array.from({ length: FRAME_COUNT }, (_, index) => {
      const frame = sceneFactories.createLightFrame();
      // Soft local illumination on the white paint follows each tube assembly.
      const floorLight = new THREE.PointLight(0xe6f3ff, 0, 24, 2);
      frame.group.add(floorLight);
      frame.group.position.set(0, 2, 2 - index * FRAME_SPACING);
      scene.add(frame.group);
      return { ...frame, floorLight };
    });
    const floor = createMatteFloor();
    const mist = createMist();
    scene.add(floor.mesh, mist.group);

    const target = new THREE.WebGLRenderTarget(1, 1, { type: THREE.HalfFloatType, samples: 2 });
    const composer = new EffectComposer(renderer, target);
    const renderPass = new RenderPass(scene, camera);
    const bloom = new UnrealBloomPass(new THREE.Vector2(1, 1), 0.24, 0.16, 1.1);
    const output = new OutputPass();
    output.material.depthTest = false;
    output.material.depthWrite = false;
    const grain = createFilmGrain();
    composer.addPass(renderPass);
    composer.addPass(bloom);
    composer.addPass(output);
    composer.addPass(grain);

    const resize = () => {
      const width = Math.max(1, host.clientWidth), height = Math.max(1, host.clientHeight);
      camera.aspect = width / height;
      camera.updateProjectionMatrix();
      renderer.setSize(width, height);
      composer.setSize(width, height);
      // Only the viewport changes; world-space squares never stretch or fit to it.
    };
    const observer = new ResizeObserver(resize);
    observer.observe(host);
    resize();

    const engine = new sceneFactories.LightFrameEngine();
    const bins = getLogFrequencyBins({ fftSize: 8192, sampleRate, minFreq: 20, maxFreq: 20000, numPoints: 256 });
    const frequencies = bins.map(bin => bin * sampleRate / 8192);
    const values = new Array<number>(256).fill(0.5);
    let previous = performance.now(), elapsed = 0;

    renderer.setAnimationLoop((now) => {
      const delta = Math.max(0, Math.min(0.08, (now - previous) / 1000));
      previous = now;
      if (document.hidden) return;
      elapsed += delta;
      const settings = current.current;
      const spectrum = settings.playing ? getFrequencyData() : null;
      for (let i = 0; i < values.length; i++) {
        const db = spectrum?.[bins[i]!];
        values[i] = db !== undefined && Number.isFinite(db) ? 0.5 + (Math.max(-100, Math.min(0, db)) + 100) / 200 : 0.5;
      }
      const audio = readLightFrameAudio(values, settings, undefined, frequencies);
      const state = engine.step(audio, delta, settings.mode, settings.threshold, settings.rotationEnabled);
      settings.onBassIntensityChange?.(audio.bass, engine.triggered);
      settings.onHighIntensityChange?.(audio.treble, false);

      let totalLight = 0;
      for (let index = 0; index < FRAME_COUNT; index++) {
        const frame = frames[index]!;
        const angle = state.rotations[index]!;
        const tiltEase = 1 - Math.exp(-delta / 0.18);
        const tiltX = settings.rotationEnabled ? Math.sin(angle * 0.7) * 0.12 : 0;
        const tiltY = settings.rotationEnabled ? Math.sin(angle * 0.55) * 0.18 : 0;
        frame.group.rotation.x += (tiltX - frame.group.rotation.x) * tiltEase;
        frame.group.rotation.y += (tiltY - frame.group.rotation.y) * tiltEase;
        frame.group.rotation.z = angle;
        const z = state.positions[index]!;
        frame.group.position.z = z;
        frame.group.scale.setScalar(settings.mode === 4 ? 1 + state.edges[index * 4]! * 0.035 : 1);
        // Normalize to the current front/back assemblies, including wraparound.
        const kick = engine.kickAtFrame(index);
        let frameRadiance = 0;
        for (let side = 0; side < 4; side++) {
          const energy = state.edges[index * 4 + side]!;
          const brightness = lightBarRadiance(energy, kick);
          frame.setEdgeLight(side, brightness, kick);
          frameRadiance += brightness;
          totalLight += Math.max(energy, kick);
        }
        frame.floorLight.intensity = frameRadiance * 3;
      }
      const targetLight = Math.min(1, totalLight / 10);
      mist.material.uniforms.time!.value = elapsed;
      mist.material.uniforms.light!.value = targetLight;
      grain.uniforms.seed!.value.set(Math.random() * 4096, Math.random() * 4096);
      composer.render(delta);
    });

    return () => {
      renderer.setAnimationLoop(null);
      observer.disconnect();
      frames.forEach(frame => frame.dispose());
      floor.dispose();
      mist.geometry.dispose(); mist.material.dispose();
      bloom.dispose(); output.dispose(); grain.dispose(); renderPass.dispose();
      composer.dispose(); renderer.dispose();
      renderer.domElement.remove();
    };
  }, [getFrequencyData, sampleRate, sceneFactories]);

  return <div ref={container} className="three-light-stage">
    {error && <p role="alert" className="stage-error">{error}</p>}
  </div>;
}
